const INTEGER_ID = /^\d+$/;

export class PogiBuyBoxContractError extends Error {
  constructor(message) {
    super(message);
    this.name = 'PogiBuyBoxContractError';
  }
}

const fail = (message) => {
  throw new PogiBuyBoxContractError(message);
};

const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const requireRecord = (value, label) => {
  if (!isRecord(value)) fail(`${label} must be an object`);
  return value;
};

const requireString = (value, label) => {
  const normalized = String(value ?? '').trim();
  if (!normalized) fail(`${label} must be a non-empty string`);
  return normalized;
};

const requireId = (value, label) => {
  const normalized = requireString(value, label);
  if (!INTEGER_ID.test(normalized)) fail(`${label} must be a numeric Shopify ID`);
  return normalized;
};

const requireInteger = (value, label, minimum = 0) => {
  const normalized = Number(value);
  if (!Number.isSafeInteger(normalized) || normalized < minimum) {
    fail(`${label} must be a safe integer >= ${minimum}`);
  }
  return normalized;
};

const requireBoolean = (value, label) => {
  if (typeof value !== 'boolean') fail(`${label} must be a boolean`);
  return value;
};

const optionalInteger = (value, label, minimum = 0) => {
  if (value === null || value === undefined || value === '') return null;
  return requireInteger(value, label, minimum);
};

const requireUnique = (items, keyFor, label) => {
  const seen = new Set();
  for (const item of items) {
    const key = keyFor(item);
    if (seen.has(key)) fail(`${label} contains duplicate ${key}`);
    seen.add(key);
  }
};

const normalizeMoney = (input, label, minimum = 0) => {
  const money = requireRecord(input, label);
  return {
    shopifyAmount: requireInteger(money.shopifyAmount, `${label}.shopifyAmount`, minimum),
    formatted: requireString(money.formatted, `${label}.formatted`),
  };
};

const normalizeQuantityRule = (input, label) => {
  const rule = requireRecord(input, label);
  const min = requireInteger(rule.min, `${label}.min`, 1);
  const step = requireInteger(rule.step, `${label}.step`, 1);
  const max = optionalInteger(rule.max, `${label}.max`, 1);
  const cartQuantity = requireInteger(rule.cartQuantity ?? 0, `${label}.cartQuantity`);
  if (max !== null && max < min) fail(`${label}.max must be null or >= min`);
  return { min, max, step, cartQuantity };
};

const selectionSignature = (axes, selections) => axes
  .map((axis) => `${axis.key}:${selections[axis.key]}`)
  .join('|');

/**
 * Validate and normalize the Liquid-emitted Buy Box v2 contract. Shopify IDs
 * are normalized to strings so Liquid numbers and DOM dataset strings compare
 * identically without unsafe numeric coercion.
 */
export function normalizePogiBuyBoxContract(input) {
  const raw = requireRecord(input, 'contract');
  if (raw.version !== 2) fail('contract.version must equal 2');

  const rawInstance = requireRecord(raw.instance, 'contract.instance');
  const instance = {
    sectionId: requireString(rawInstance.sectionId, 'contract.instance.sectionId'),
    formId: requireString(rawInstance.formId, 'contract.instance.formId'),
    productId: requireId(rawInstance.productId, 'contract.instance.productId'),
    handle: requireString(rawInstance.handle, 'contract.instance.handle'),
    selectedVariantId: requireId(rawInstance.selectedVariantId, 'contract.instance.selectedVariantId'),
  };

  const rawMarket = requireRecord(raw.market, 'contract.market');
  const market = {
    currency: requireString(rawMarket.currency, 'contract.market.currency').toUpperCase(),
    displayLocale: requireString(rawMarket.displayLocale, 'contract.market.displayLocale'),
  };

  if (!Array.isArray(raw.axes) || raw.axes.length === 0) fail('contract.axes must be a non-empty array');
  const axes = raw.axes.map((rawAxis, axisIndex) => {
    const axis = requireRecord(rawAxis, `contract.axes[${axisIndex}]`);
    if (!Array.isArray(axis.values) || axis.values.length === 0) {
      fail(`contract.axes[${axisIndex}].values must be a non-empty array`);
    }
    const values = axis.values.map((rawValue, valueIndex) => {
      const value = requireRecord(rawValue, `contract.axes[${axisIndex}].values[${valueIndex}]`);
      return {
        optionValueId: requireId(
          value.optionValueId,
          `contract.axes[${axisIndex}].values[${valueIndex}].optionValueId`,
        ),
        shopifyValue: requireString(
          value.shopifyValue,
          `contract.axes[${axisIndex}].values[${valueIndex}].shopifyValue`,
        ),
        label: requireString(value.label, `contract.axes[${axisIndex}].values[${valueIndex}].label`),
        available: requireBoolean(
          value.available,
          `contract.axes[${axisIndex}].values[${valueIndex}].available`,
        ),
      };
    });
    requireUnique(values, (value) => value.optionValueId, `contract.axes[${axisIndex}].values`);
    requireUnique(values, (value) => value.shopifyValue, `contract.axes[${axisIndex}].values`);
    return {
      key: requireString(axis.key, `contract.axes[${axisIndex}].key`),
      kind: requireString(axis.kind, `contract.axes[${axisIndex}].kind`),
      optionPosition: requireInteger(axis.optionPosition, `contract.axes[${axisIndex}].optionPosition`, 1),
      name: requireString(axis.name, `contract.axes[${axisIndex}].name`),
      values,
    };
  });
  requireUnique(axes, (axis) => axis.key, 'contract.axes');
  requireUnique(axes, (axis) => axis.optionPosition, 'contract.axes');

  if (!Array.isArray(raw.plans)) fail('contract.plans must be an array');
  const plans = raw.plans.map((rawPlan, planIndex) => {
    const plan = requireRecord(rawPlan, `contract.plans[${planIndex}]`);
    return {
      id: requireId(plan.id, `contract.plans[${planIndex}].id`),
      groupId: requireString(plan.groupId, `contract.plans[${planIndex}].groupId`),
      name: requireString(plan.name, `contract.plans[${planIndex}].name`),
    };
  });
  requireUnique(plans, (plan) => plan.id, 'contract.plans');
  const planIds = new Set(plans.map((plan) => plan.id));

  if (!Array.isArray(raw.variants) || raw.variants.length === 0) {
    fail('contract.variants must be a non-empty array');
  }
  const variants = raw.variants.map((rawVariant, variantIndex) => {
    const variant = requireRecord(rawVariant, `contract.variants[${variantIndex}]`);
    const selectionsInput = requireRecord(variant.selections, `contract.variants[${variantIndex}].selections`);
    const selections = {};
    for (const axis of axes) {
      const shopifyValue = requireString(
        selectionsInput[axis.key],
        `contract.variants[${variantIndex}].selections.${axis.key}`,
      );
      if (!axis.values.some((value) => value.shopifyValue === shopifyValue)) {
        fail(`contract.variants[${variantIndex}] references unknown ${axis.key} value ${shopifyValue}`);
      }
      selections[axis.key] = shopifyValue;
    }
    const extraSelection = Object.keys(selectionsInput).find((key) => !axes.some((axis) => axis.key === key));
    if (extraSelection) fail(`contract.variants[${variantIndex}] has unknown selection axis ${extraSelection}`);

    if (!Array.isArray(variant.allocations)) {
      fail(`contract.variants[${variantIndex}].allocations must be an array`);
    }
    const allocations = variant.allocations.map((rawAllocation, allocationIndex) => {
      const label = `contract.variants[${variantIndex}].allocations[${allocationIndex}]`;
      const allocation = requireRecord(rawAllocation, label);
      const planId = requireId(allocation.planId, `${label}.planId`);
      if (!planIds.has(planId)) fail(`${label}.planId does not exist in contract.plans`);
      return {
        planId,
        price: normalizeMoney(allocation.price, `${label}.price`),
        savings: normalizeMoney(allocation.savings, `${label}.savings`, Number.MIN_SAFE_INTEGER),
      };
    });
    requireUnique(allocations, (allocation) => allocation.planId, `contract.variants[${variantIndex}].allocations`);

    return {
      id: requireId(variant.id, `contract.variants[${variantIndex}].id`),
      available: requireBoolean(variant.available, `contract.variants[${variantIndex}].available`),
      selections,
      price: normalizeMoney(variant.price, `contract.variants[${variantIndex}].price`),
      allocations,
      quantityRule: normalizeQuantityRule(
        variant.quantityRule,
        `contract.variants[${variantIndex}].quantityRule`,
      ),
      mediaId: variant.mediaId == null ? null : requireId(variant.mediaId, `contract.variants[${variantIndex}].mediaId`),
      subscriptionConfig: String(variant.subscriptionConfig ?? ''),
    };
  });
  requireUnique(variants, (variant) => variant.id, 'contract.variants');
  requireUnique(variants, (variant) => selectionSignature(axes, variant.selections), 'contract.variants selections');
  if (!variants.some((variant) => variant.id === instance.selectedVariantId)) {
    fail('contract.instance.selectedVariantId does not exist in contract.variants');
  }

  const i18n = isRecord(raw.i18n) ? Object.fromEntries(
    Object.entries(raw.i18n).map(([key, value]) => [key, String(value ?? '')]),
  ) : {};

  return { version: 2, instance, market, axes, variants, plans, i18n };
}

export function readPogiBuyBoxContract(root, selector = '[data-pogi-v2-data]') {
  if (!(root instanceof Element)) fail('Buy Box contract root must be an Element');
  const node = root.querySelector(selector);
  if (!node?.textContent) fail(`Buy Box contract script ${selector} is missing`);
  let parsed;
  try {
    parsed = JSON.parse(node.textContent);
  } catch {
    fail('Buy Box contract is not valid JSON');
  }
  return normalizePogiBuyBoxContract(parsed);
}

export class PogiBuyBoxModel {
  constructor(contract) {
    this.contract = normalizePogiBuyBoxContract(contract);
    this.axes = this.contract.axes;
    this.variantsById = new Map(this.contract.variants.map((variant) => [variant.id, variant]));
    this.plansById = new Map(this.contract.plans.map((plan) => [plan.id, plan]));
    this.variantsBySelection = new Map(
      this.contract.variants.map((variant) => [selectionSignature(this.axes, variant.selections), variant]),
    );
  }

  variantById(variantId) {
    return this.variantsById.get(String(variantId ?? '')) ?? null;
  }

  planById(planId) {
    return this.plansById.get(String(planId ?? '')) ?? null;
  }

  resolveVariant(selections) {
    if (!isRecord(selections)) return null;
    for (const axis of this.axes) {
      if (typeof selections[axis.key] !== 'string' || !selections[axis.key]) return null;
    }
    return this.variantsBySelection.get(selectionSignature(this.axes, selections)) ?? null;
  }

  allocationFor(variantId, planId) {
    const variant = this.variantById(variantId);
    const normalizedPlanId = String(planId ?? '');
    if (!variant || !normalizedPlanId || !this.plansById.has(normalizedPlanId)) return null;
    return variant.allocations.find((allocation) => allocation.planId === normalizedPlanId) ?? null;
  }

  quantityState(variantId, requestedQuantity, cartQuantityOverride = null) {
    const variant = this.variantById(variantId);
    if (!variant) fail(`Unknown variant ${variantId}`);
    const { min, max, step } = variant.quantityRule;
    const cartQuantity = cartQuantityOverride === null
      ? variant.quantityRule.cartQuantity
      : requireInteger(cartQuantityOverride, `Variant ${variant.id} cart quantity`);
    const remaining = max === null ? null : max - cartQuantity;
    const canAdd = variant.available && (remaining === null || remaining >= min);
    const requested = Number.isFinite(Number(requestedQuantity)) ? Number(requestedQuantity) : min;
    const capped = Math.max(min, Math.min(remaining ?? Infinity, requested));
    const value = min + Math.floor((capped - min) / step) * step;
    return { value, min, max, step, cartQuantity, remaining, canAdd };
  }

  stepQuantity(variantId, currentQuantity, direction, cartQuantityOverride = null) {
    const state = this.quantityState(variantId, currentQuantity, cartQuantityOverride);
    const delta = direction < 0 ? -state.step : state.step;
    return this.quantityState(variantId, state.value + delta, cartQuantityOverride);
  }

  assertPlanAllocation(variantId, planId) {
    if (planId == null || planId === '') return null;
    const allocation = this.allocationFor(variantId, planId);
    if (!allocation) fail(`Selling plan ${planId} is not allocated to variant ${variantId}`);
    return allocation;
  }
}
